/** Minimal in-place radix-2 FFT for real-input magnitude spectra. */
export class FFT {
  private readonly n: number;
  private readonly cosTable: Float32Array;
  private readonly sinTable: Float32Array;
  private readonly rev: Uint32Array;
  readonly re: Float32Array;
  readonly im: Float32Array;

  constructor(n: number) {
    if ((n & (n - 1)) !== 0) throw new Error("FFT size must be a power of 2");
    this.n = n;
    this.re = new Float32Array(n);
    this.im = new Float32Array(n);
    this.cosTable = new Float32Array(n / 2);
    this.sinTable = new Float32Array(n / 2);
    for (let i = 0; i < n / 2; i++) {
      this.cosTable[i] = Math.cos((2 * Math.PI * i) / n);
      this.sinTable[i] = Math.sin((2 * Math.PI * i) / n);
    }
    this.rev = new Uint32Array(n);
    const bits = Math.log2(n);
    for (let i = 0; i < n; i++) {
      let r = 0;
      for (let b = 0; b < bits; b++) r |= ((i >> b) & 1) << (bits - 1 - b);
      this.rev[i] = r;
    }
  }

  /** Computes magnitudes of the first n/2 bins from a real input frame. */
  magnitudes(input: Float32Array, out: Float32Array): void {
    const { n, re, im, rev, cosTable, sinTable } = this;
    for (let i = 0; i < n; i++) {
      re[i] = input[rev[i]!]!;
      im[i] = 0;
    }
    for (let size = 2; size <= n; size <<= 1) {
      const half = size >> 1;
      const step = n / size;
      for (let i = 0; i < n; i += size) {
        for (let j = i, k = 0; j < i + half; j++, k += step) {
          const c = cosTable[k]!;
          const s = sinTable[k]!;
          const l = j + half;
          const tre = re[l]! * c + im[l]! * s;
          const tim = im[l]! * c - re[l]! * s;
          re[l] = re[j]! - tre;
          im[l] = im[j]! - tim;
          re[j] = re[j]! + tre;
          im[j] = im[j]! + tim;
        }
      }
    }
    const half = n >> 1;
    for (let i = 0; i < half; i++) {
      out[i] = Math.hypot(re[i]!, im[i]!);
    }
  }
}
